/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, Word, __MSS_ENVIRONMENT__, __MSS_API_BASE_URL__, __MSS_APP_VERSION__, __MSS_SENTRY_DSN__, __MSS_SENTRY_TRACES_SAMPLE_RATE__ */

import React from "react";
import ReactDOM from "react-dom/client";
import debounce from "lodash/debounce";
import { startBackgroundInit } from "src/services";
import { onSelectionChanged } from "src/taskpane/handlers";
import { TaskpaneApp } from "src/taskpane/TaskpaneApp";
import * as Sentry from "@sentry/react";

Sentry.init({
  // Empty in a local or build-verification build, which disables the SDK.
  dsn: __MSS_SENTRY_DSN__,
  // Every environment reports into the same project, so tag which is which:
  // "local", "dev", "test" or "prod".
  environment: __MSS_ENVIRONMENT__,
  // Matches the API's release, so one run's two halves group together. Only CI
  // supplies the version, so a local build reports no release rather than a
  // versionless "mss@".
  release: __MSS_APP_VERSION__ ? `mss@${__MSS_APP_VERSION__}` : undefined,
  // Webpack's hot-reload runtime throws this when an update lands mid-reload.
  // It only exists in a dev-server build — there is no HMR runtime in a
  // production bundle — so it is never a real report, only triage noise.
  ignoreErrors: [/currentUpdateRuntime/],
  dataCollection: {
    // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
    // userInfo: false,
    // httpBodies: []
  },
  // Attaches `sentry-trace`/`baggage` to outgoing requests, which is what lets
  // the API's report of a run share a trace with this one.
  integrations: [Sentry.browserTracingIntegration()],
  // Per environment, and the only rate that matters for a styling run: this
  // side's decision travels to the API, which inherits it rather than rolling
  // its own. So a run declined here sends no spans from either side — including
  // the API's per-LLM-call spans, the expensive half of the picture — while a
  // run sampled here is traced end to end even though the API's own rate is 0.
  // A run is ~70 spans here plus ~200 there.
  tracesSampleRate: __MSS_SENTRY_TRACES_SAMPLE_RATE__,
  // Scoped to our own API. Left unset, the SDK attaches trace headers to every
  // request, including Okta's — an unexpected header there turns a token request
  // into one Okta's CORS preflight can reject, which would break sign-in.
  // Dev builds call a relative `/api/...` through the webpack proxy; deployed
  // builds use the absolute base.
  tracePropagationTargets: [__MSS_API_BASE_URL__ || /^\/api\//],
});

Office.onReady(async (info) => {
  if (info.host === Office.HostType.Word) {
    // Mount React immediately — the user sees the UI in <100ms.
    // All document init (lock cleanup, XML part, paragraph tagging) runs in the background.
    const root = ReactDOM.createRoot(document.getElementById("app-root")!);
    root.render(React.createElement(TaskpaneApp));

    await startBackgroundInit();

    // POC: expose tagging helpers on window for DevTools console access
    // Usage: await __mss_tagParagraphs()  /  await __mss_diagnoseTags()
    // (window as any).__mss_tagParagraphs = tagAllParagraphs;
    // (window as any).__mss_diagnoseTags = diagnoseParagraphTags;
    // (window as any).__mss_logOoxml = logTaggedOoxml;
    // (window as any).__mss_getTagMap = getTagToLocalIdMap;
  }

  Office.context.document.addHandlerAsync(
    Office.EventType.DocumentSelectionChanged,
    onSelectionChanged
  );
});
